import { useState } from 'react';
import CopyToClipboard from 'react-copy-to-clipboard';
import { Button, Center, Spinner } from 'src/components/atoms';
import { VaultLogo } from 'src/components/foundations/VaultLogo/VaultLogo';
import { createDuration, diff, TimeUnit } from 'src/utils/time';
import { TOKENS } from '../../../api';
import axios from '../../../axios';
import { Timer } from '../../atomsLegacy';
import {
  ButtonContainer,
  CopyLink,
  Header,
  ShortTokenBox,
  ShortTokenContainer,
  ShortTokenGenerateBox,
  ShortTokenHeading,
  ShortTokenImages,
  ShortTokenInstructions,
  ShortTokenMainText,
  ShortTokenSurface,
  ShortTokenText,
  VaultLogoWrapper,
} from './ShortToken.styles';

export interface Props {
  token: string;
}

export const ShortToken = ({ token }: Props) => {
  const [loading, setLoading] = useState(false);
  const [invalid, setInvalid] = useState(false);
  const [expiresAt, setExpiresAt] = useState(createDuration({ seconds: 0 }));
  const [showButton, setShowButton] = useState(true);
  const [copied, setCopied] = useState(false);
  const [shortToken, setShortToken] = useState('INVALID');
  const [error, setError] = useState(
    'Something went wrong. Please try again later.',
  );

  const onClick = async () => {
    setLoading(true);
    try {
      const result = await axios.post(TOKENS, { token });
      if (result.status === 201) {
        setShortToken(result.data.data.token);
        const timeLeft = new Date(
          diff(
            new Date(result.data.data.createdAt),
            new Date(),
            TimeUnit.milliseconds,
          ),
        );
        setExpiresAt(
          createDuration({
            seconds: timeLeft.getSeconds(),
            minutes: timeLeft.getMinutes(),
            hours: timeLeft.getHours(),
          }),
        );
        setInvalid(false);
        setLoading(false);
        setShowButton(false);
      } else {
        setInvalid(true);
        setLoading(false);
      }
    } catch (err) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const error = (err as any)?.response?.data?.errors[0]?.message
        ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (err as any).response.data.errors[0].message
        : 'Something went wrong. Please try again later.';
      setInvalid(true);
      setLoading(false);
      setError(error);
      setShowButton(false);
    }
  };
  return (
    <div>
      {loading && (
        <Center>
          <Spinner variant="secondary" size="xlarge" />
        </Center>
      )}
      {!loading && showButton && (
        <ShortTokenGenerateBox>
          <VaultLogoWrapper>
            <VaultLogo color="N0" size="large" />
          </VaultLogoWrapper>
          <ShortTokenMainText>
            The activation code allows you to get started with Vault. This code
            is unique to you and cannot be shared.
          </ShortTokenMainText>
          <ButtonContainer>
            <Button
              variant="primary"
              size="medium"
              data-testid="generate-activation-code"
              onClick={onClick}
            >
              Generate activation code
            </Button>
          </ButtonContainer>
          <ShortTokenMainText>
            You can report misconduct or concerns and submit them to your
            employer.
          </ShortTokenMainText>
          <ShortTokenMainText>
            You choose whether to reveal your identity or remain anonymous. You
            are in control.
          </ShortTokenMainText>
        </ShortTokenGenerateBox>
      )}
      {!loading && !showButton && (
        <ShortTokenContainer>
          {invalid ? (
            <Header data-testid="token-error-message"> {error} </Header>
          ) : (
            <ShortTokenBox>
              <VaultLogo size="xsmall" color="N800" />
              <ShortTokenMainText>
                <p>
                  Please copy and enter this personal activation code on Vault
                  Platform app following the 3-step process below.
                </p>
                <p>The code will be valid for the next</p>
              </ShortTokenMainText>
              <Timer interval={expiresAt} />
              <Header id="code" data-testid="short-token-header">
                {shortToken}
              </Header>
              <CopyToClipboard
                text={shortToken ? shortToken : 'Invalid'}
                onCopy={() => setCopied(true)}
              >
                {copied ? (
                  <CopyLink>Copied</CopyLink>
                ) : (
                  <CopyLink>Copy code</CopyLink>
                )}
              </CopyToClipboard>
            </ShortTokenBox>
          )}
        </ShortTokenContainer>
      )}

      {!showButton && !invalid && (
        <ShortTokenInstructions>
          <ShortTokenSurface>
            <ShortTokenHeading>1. Head to the app</ShortTokenHeading>
            <ShortTokenText>
              Ensure you have downloaded the Vault Platform app on your mobile
              device.
            </ShortTokenText>

            <ShortTokenImages>
              <a href="https://play.google.com/store/apps/details?id=com.vaultplatform.app">
                <img alt="" width="225px" src="/assets/google-play-badge.png" />
              </a>
              <a href="https://itunes.apple.com/gb/app/vault-platform/id1444463273">
                <img alt="" width="200px" src="/assets/apple-badge.png" />
              </a>
            </ShortTokenImages>
          </ShortTokenSurface>

          <ShortTokenSurface>
            <ShortTokenHeading>2. Tap Get Started</ShortTokenHeading>

            <ShortTokenImages>
              <img alt="" style={{ height: 400 }} src="/assets/iphone1.png" />
            </ShortTokenImages>
            <br />
          </ShortTokenSurface>

          <ShortTokenSurface>
            <ShortTokenHeading>3. Enter your activation code</ShortTokenHeading>

            <ShortTokenImages>
              <img alt="" style={{ height: 400 }} src="/assets/iphone2.png" />
            </ShortTokenImages>

            <br />
          </ShortTokenSurface>
        </ShortTokenInstructions>
      )}
    </div>
  );
};
